import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { PAYROLL_ACTION_SECTION } from 'src/constants/FormNames';
import { REDUCER_NAME, fetchPayslipByMonth } from 'src/reducers/employees/employees';
import { getItem, isLoading } from 'src/reducers/common/itemReducer';
import { EMPLOYEE_MY_DATA_DOWNLOAD_PAY } from 'src/constants/EndpointUrls';
import Loading from 'src/components/Loading';

import ActionButton from 'jbc-front/components/ActionButton';
import { Task, Pulldown } from 'jbc-front/components/icons';
import { employeeDisplay } from 'src/utils/Utils';
import PreviewPdfModalContainer from 'src/containers/PreviewPdfModalContainer';
import balloonStyle from 'src/features/EmployeePays/components/EmployeePayForm.scss';
import Balloon from 'src/features/EmployeePays/components/Balloon';
import { SelectField } from 'src/jbc-front/components/Form';
import { ConfirmedOptionRenderer } from 'src/components';
import styles from './PayrollActionSection.scss';

const PayrollActionSection = ({ employeesState, dispatch, client, isFetchingPayslip }) => {
  const { employee, allPayrolls, selectingPayroll } = employeesState;
  const [employeePayId, setEmployeePayId] = useState(selectingPayroll?.id);
  const [isPdfPreview, setIsPdfPreview] = useState(false);
  const [isPdfExportBalloonOpen, setIsPdfExportBalloonOpen] = useState(false);
  const [isEnglish, setIsEnglish] = useState(false);

  const isDownloadable = selectingPayroll?.isReleased;

  const { t, i18n } = useTranslation();
  const clientName = client.officeName;

  const showPreview = () => setIsPdfPreview(true);
  const hidePreview = () => setIsPdfPreview(false);
  const showBalloon = () => setIsPdfExportBalloonOpen(true);
  const hideBalloon = () => setIsPdfExportBalloonOpen(false);

  const onChangeEmployeePay = useCallback(async selectedId => {
    if (!selectedId) return;
    setEmployeePayId(selectedId);
    await dispatch(fetchPayslipByMonth(selectedId));
  });

  useEffect(
    () => {
      if (selectingPayroll.id) setEmployeePayId(selectingPayroll.id);
    },
    [selectingPayroll]
  );

  return (
    <div>
      <div className={styles.wrap}>
        <div className={styles.action}>
          <div className={styles.actionRow}>
            <div className={styles.payrollSelectWrapper}>
              <SelectField
                noLabel
                name="employeePayId"
                options={allPayrolls}
                placeholder={null}
                onChange={onChangeEmployeePay}
                optionRenderer={option => <ConfirmedOptionRenderer option={option} />}
                addBlankOption={false}
              />
            </div>
            {isDownloadable && (
              <div className={styles.pdfButtonWrap}>
                <ActionButton primary icon={<Task size={15} />} onClick={showBalloon} className={styles.button}>
                  {t('features.MyData.components.payroll.PayrollActionSection.pdf_export')}

                  {isPdfExportBalloonOpen && (
                    <Balloon onEmployee hide={hideBalloon}>
                      <ul className={balloonStyle.links}>
                        <li>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            onClick={() => {
                              setIsEnglish(false);
                              showPreview();
                            }}
                          >
                            <Pulldown size={10} /> {t('features.EmployeeBonus.ShowBonusFbDataPage.export_vi')}
                          </button>
                        </li>
                        <li>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            onClick={() => {
                              setIsEnglish(true);
                              showPreview();
                            }}
                          >
                            <Pulldown size={10} /> {t('features.EmployeeBonus.ShowBonusFbDataPage.export_en')}
                          </button>
                        </li>
                      </ul>
                    </Balloon>
                  )}
                </ActionButton>
              </div>
            )}
          </div>
          <PreviewPdfModalContainer
            header={t('features.MyData.components.payroll.PayrollActionSection.payslip')}
            isOpen={isPdfPreview}
            hideModal={hidePreview}
            url={`${EMPLOYEE_MY_DATA_DOWNLOAD_PAY}?id=${employeePayId}&language=${isEnglish ? 'en' : 'vi'}`}
          />
        </div>

        <div className={styles.summary}>
          {isFetchingPayslip ? (
            <Loading />
          ) : (
            <>
              <div className={styles.title}>
                {t('features.MyData.components.payroll.PayrollActionSection.payslip')}
                {moment(selectingPayroll?.displayYearmonthOnSlip).format(' MM/YYYY')}
              </div>
              <div className={styles.date}>
                {t('features.MyData.components.payroll.PayrollActionSection.payment_date')} {moment(selectingPayroll?.paymentDate).format('DD/MM/YYYY')}
              </div>
              <dl className={styles.user}>
                <dt>{t('features.MyData.components.payroll.PayrollActionSection.staff_code')}</dt>
                <dd>: {employee?.staffCode}</dd>
              </dl>
              <dl className={styles.user}>
                <dt>{t('features.MyData.components.payroll.PayrollActionSection.name')}</dt>
                <dd>: {employeeDisplay.fullName(employee, i18n)}</dd>
              </dl>
              <div className={styles.client}>{clientName}</div>{' '}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const employeesState = getItem(REDUCER_NAME, state) || {};
  return {
    client: state.currentClient.item.data,
    employeesState,
    isFetchingPayslip: isLoading(REDUCER_NAME, state),
    initialValues: {
      employeePayId: employeesState.selectingPayroll?.id
    }
  };
};

const PayrollActionSectionForm = reduxForm({
  form: PAYROLL_ACTION_SECTION,
  enableReinitialize: true
})(PayrollActionSection);

export default connect(mapStateToProps)(PayrollActionSectionForm);
